import RegisterTextBox from "../../../UI/common/Inputs/register-textbox";
import classes from "./register-user-info-form.module.css";
import RegisterSelectBox from "../../../UI/common/Inputs/register-select";
import { devbackendUrl, Genders, Nationalities } from "../../../utils/constants";
import { useState, useEffect } from "react";
import Calendar from "react-calendar";
import BlueButton from "../../../UI/common/Buttons/blue-button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthUtils } from "../../../utils/helper_functions";
import CategorySelector from "../../../UI/common/Inputs/category-selector";
import { AUTH_REDIRECT_PARAM, getSafeRedirectPath } from "../../../utils/auth-redirect";

interface Category {
  id: number;
  name: string;
  description: string;
}

const RegisterUserInfoForm: React.FC<{
  parentClassName?: string;
}> = (props) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [gender, setGender] = useState<string | null>(null);
  const [nationality, setNationality] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = getSafeRedirectPath(searchParams.get(AUTH_REDIRECT_PARAM), "/");


  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${devbackendUrl}user/categories`, {
          headers: {
            Authorization: `Bearer ${AuthUtils.getAuthToken()}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const handleDateChange = (value: Date | Date[] | null, event?: React.MouseEvent) => {
    // Ensure value is a single Date and not null or an array
    if (value instanceof Date) {
      setSelectedDate(value);
    }
    setShowCalendar(false); // Hide calendar after selecting a date
  };

  const formatDate = (date: Date | null) => {
    if (date) {
      // Convert to YYYY-MM-DD format
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed, so add 1
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return ""; // Return empty string if date is null
  };


  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const userData = {
      birthday: selectedDate ? formatDate(selectedDate) : null,
      nationality,
      gender,
      favoriteCategoryIds: selectedCategoryIds,
    };
    console.log(userData);

    try {
      const response = await fetch(`${devbackendUrl}user/info`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${AuthUtils.getAuthToken()}`, // Replace with your auth token logic
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        console.log(response);
        throw new Error("Failed to update user information");
      }

      navigate(redirectTo, { replace: true }); // Redirect on success
    } catch (error) {
      console.error(error);
      alert("An error occurred while updating user information.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form method="PATCH" onSubmit={handleSubmit} className={props.parentClassName ?? ""}>
      <div className={classes.wrapper}>
        <div className={classes.leftColumn}>
          <div className={classes.textBoxWrapper}>
            <RegisterTextBox
              type="text"
              id="birthday"
              placeholder="Select your birthday"
              value={formatDate(selectedDate)}
              onFocus={() => setShowCalendar(true)}
              readOnly // Make the text box non-editable
              onValidationChange={() => {}} // No validation needed
              validateFunction={() => ({ isValid: true, errorMessage: "" })} // No validation needed
            />
            {showCalendar && (
              <div className={classes.calendarContainer}>
                <Calendar
                  onChange={handleDateChange as any}
                  value={selectedDate}
                  className="custom-calendar"
                />
              </div>
            )}
          </div>
          <RegisterSelectBox
            id="gender"
            options={Genders}
            onChange={(value) => setGender(value)} // Pass the selected value to the parent
          />
          <RegisterSelectBox
            id="nationality"
            options={Nationalities}
            onChange={(value) => setNationality(value)} // Pass the selected value to the parent
          />
          <BlueButton isDisabled={isSubmitting} type="submit" onClick={() => {}} text="Finish" />
        </div>
        <div className={classes.rightColumn}>
          <CategorySelector
            categories={categories}
            selectedCategoryIds={selectedCategoryIds}
            onChange={setSelectedCategoryIds}
          />
        </div>
      </div>
    </form>
  );
};

export default RegisterUserInfoForm;
